import { Box, Card, CardContent, Typography } from '@mui/joy';
import { DownloadSimple, Eye, FileArrowUp, Trash } from '@phosphor-icons/react';
import convertSize from 'convert-size';
import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useGenerateExport } from 'cli/hooks';
import { Button, Confirmation, IDocumentFile, PreviewCsv, PreviewPdf } from 'core/components';
import { FilePreview, ImagePreview, TextEllipsis } from 'core/elements';
import { MimeType } from 'core/enums';
import { formatDate, getDomainUrl } from 'core/utils';
import { Color, Level } from 'theme/enums';

interface IProps {
  file: IDocumentFile;
  onRemove?: (fileId: number) => Promise<void>;
  extraInfo?: ReactNode;
  sourceLabel?: string;
  sourceLabelColor?: Color;
}

const ImagePreviewMimeTypes = [MimeType.GIF, MimeType.JPEG, MimeType.PNG, MimeType.SVG];
const PreviewMimeTypes = [MimeType.PDF, MimeType.CSV, ...ImagePreviewMimeTypes];

export const DocumentCard = ({
  file,
  onRemove,
  extraInfo,
  sourceLabel,
  sourceLabelColor = Color.PRIMARY,
}: IProps) => {
  const [showPreview, setShowPreview] = useState(false);
  const [showRemoveConfirmation, setShowRemoveConfirmation] = useState(false);

  const { t } = useTranslation();

  const { downloadFilePath } = useGenerateExport();

  const downloadFileUrl = file.fileId ? downloadFilePath(file.fileId.toString()) : undefined;
  const displayPath = file.fileFullPath?.startsWith('/')
    ? file.fileFullPath
    : `/${file.fileFullPath}`;
  const displayUrl = file.fileFullPath ? `${getDomainUrl()}${displayPath}` : downloadFileUrl;
  const downloadUrl =
    downloadFileUrl ??
    (displayUrl ? `${displayUrl}${displayUrl.includes('?') ? '&' : '?'}download=1` : undefined);

  const downloadFile = () => {
    if (!downloadUrl) {
      return;
    }

    window.location.href = downloadUrl;
  };

  const handleOnRemove = async () => {
    setShowRemoveConfirmation(false);

    await onRemove(file.id);
  };

  return (
    <>
      <Card sx={{ width: '200px' }}>
        <CardContent sx={{ gap: 1, justifyContent: 'space-between' }}>
          <Box sx={{ gap: 0.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Button
                  color={Color.PRIMARY}
                  icon={<DownloadSimple size={24} />}
                  onClick={() => downloadFile()}
                  sx={{
                    svg: {
                      height: '24px !important',
                      width: '24px !important',
                    },
                  }}
                />
                {PreviewMimeTypes.includes(file.fileMime) && (
                  <Button
                    color={Color.PRIMARY}
                    icon={<Eye size={24} />}
                    onClick={() => setShowPreview(!showPreview)}
                    sx={{
                      svg: {
                        height: '24px !important',
                        width: '24px !important',
                      },
                    }}
                  />
                )}
              </Box>

              {onRemove && (
                <Button
                  color={Color.DANGER}
                  icon={<Trash size={24} />}
                  onClick={() => setShowRemoveConfirmation(true)}
                  sx={{
                    svg: {
                      height: '24px !important',
                      width: '24px !important',
                    },
                  }}
                />
              )}
            </Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '10px',
              }}
            >
              <FilePreview
                fileType={file.fileMime}
                fileUrl={displayUrl}
                fileName={file.fileName}
                onSelect={() => setShowPreview(!showPreview)}
                previewStyle={{ height: '59px', width: 'auto', maxWidth: '174px' }}
              />
            </Box>
          </Box>
          <Box sx={{ gap: 0.5 }}>
            <Box>
              <TextEllipsis
                content={file.description || file.fileName}
                maxRows={2}
                sx={{ height: '42px' }}
              />
              {file.fileSize > 0 && (
                <Typography level={Level.BODY_XS} color={Color.NEUTRAL}>
                  {convertSize(file.fileSize)}
                </Typography>
              )}
            </Box>
            <Box>
              <Typography level={Level.BODY_XS} color={sourceLabelColor}>
                <FileArrowUp weight="fill" /> {sourceLabel || t('labelUploaded')}
              </Typography>
              <Typography level={Level.BODY_SM}>{formatDate(file.fileUploadedAt, true)}</Typography>
            </Box>

            {extraInfo}
          </Box>
        </CardContent>
      </Card>

      <PreviewPdf
        isOpen={showPreview && MimeType.PDF === file.fileMime}
        onClose={() => setShowPreview(false)}
        fileName={file?.fileName}
        fileUrl={displayUrl}
        subtitle={file?.description}
      />

      <ImagePreview
        isOpen={showPreview && ImagePreviewMimeTypes.includes(file.fileMime)}
        onClose={() => setShowPreview(false)}
        fileUrl={displayUrl}
        fileName={file?.fileName}
        subtitle={file?.description}
        fileSize={file?.fileSize}
      />

      <PreviewCsv
        isOpen={showPreview && MimeType.CSV === file.fileMime}
        onClose={() => setShowPreview(false)}
        fileName={file?.fileName}
        fileUrl={downloadFilePath(file?.fileId?.toString() ?? '')}
        subtitle={file?.description}
      />

      {onRemove && (
        <Confirmation
          isOpen={showRemoveConfirmation}
          title={t('titleRemoveDocument')}
          message={
            file.description
              ? t('messageConfirmRemoveDocumentWithDescription', {
                  description: file.description,
                  fileName: file.fileName,
                })
              : t('messageConfirmRemoveDocument', { fileName: file.fileName })
          }
          confirmBtnLabel={t('buttonLabelRemove')}
          confirmBtnColor={Color.DANGER}
          onConfirm={handleOnRemove}
          onCancel={() => setShowRemoveConfirmation(false)}
        />
      )}
    </>
  );
};
